import {
  HStack,
  VStack,
  Text,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react'
import { Check, Cpu, Globe, Clock } from 'lucide-react'
import ClaudeColor from '@lobehub/icons/es/Claude/components/Color'
import CursorIcon from '@lobehub/icons/es/Cursor/components/Mono'
import type { AgentConnection } from '@/lib/api/keys'
import { DAY } from '@core/constants/expiry'

type AgentType = 'cursor' | 'claude' | 'other'

interface AgentConnectionRowProps {
  type: AgentType
  agent: AgentConnection
}

function formatLocation(ip: string | null, country: string | null): string | null {
  if (!ip && !country) return null
  if (country && ip) return `${country} · ${ip}`
  return country ?? ip
}

// Consider agent inactive if not seen in the last 24 hours
const INACTIVE_THRESHOLD_MS = DAY

function isAgentActive(lastSeenAt: number): boolean {
  return Date.now() - lastSeenAt < INACTIVE_THRESHOLD_MS
}

function renderAgentIcon(type: AgentType, size: number = 24) {
  switch (type) {
    case 'cursor':
      return <CursorIcon size={size} />
    case 'claude':
      return <ClaudeColor size={size} />
    default:
      return <Icon as={Cpu} boxSize={size / 4} color="brand.500" />
  }
}

export function AgentConnectionRow({ type, agent }: AgentConnectionRowProps) {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'
  const isActive = isAgentActive(agent.lastSeenAt)
  const location = formatLocation(agent.lastIp, agent.lastCountry)

  const getBorderColor = () => {
    if (isActive) return isDark ? 'green.700' : 'green.200'
    return isDark ? 'orange.700' : 'orange.200'
  }

  const getStatusColor = () => {
    if (isActive) return 'green.500'
    return 'orange.500'
  }

  return (
    <HStack
      w="100%"
      p={4}
      bg={isDark ? 'stone.700' : 'stone.50'}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={getBorderColor()}
      justify="space-between"
    >
      <HStack spacing={3}>
        {renderAgentIcon(type, 28)}
        <VStack align="start" spacing={0}>
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color={isDark ? 'stone.200' : 'stone.700'}>
              {agent.name}
            </Text>
            {agent.version && (
              <Text fontSize="xs" color="stone.500">
                v{agent.version}
              </Text>
            )}
            {!isActive && (
              <Tooltip label="No activity in the last 24 hours" placement="top" hasArrow>
                <HStack spacing={1} cursor="help">
                  <Icon as={Clock} boxSize={3} color="orange.500" />
                  <Text fontSize="xs" color="orange.500">
                    Inactive
                  </Text>
                </HStack>
              </Tooltip>
            )}
          </HStack>
          <HStack spacing={1}>
            <Text fontSize="xs" color={isDark ? 'stone.400' : 'stone.500'}>
              {agent.requestCount.toLocaleString()} requests · Last seen {new Date(agent.lastSeenAt).toLocaleDateString()}
            </Text>
            {location && (
              <Tooltip label={`Last connection from ${location}`} placement="top" hasArrow>
                <HStack spacing={1} ml={1} cursor="help">
                  <Icon as={Globe} boxSize={3} color={isDark ? 'stone.500' : 'stone.400'} />
                  <Text fontSize="xs" color={isDark ? 'stone.500' : 'stone.400'}>
                    {agent.lastCountry ?? 'Unknown'}
                  </Text>
                </HStack>
              </Tooltip>
            )}
          </HStack>
        </VStack>
      </HStack>
      <Icon as={Check} boxSize={5} color={getStatusColor()} />
    </HStack>
  )
}
